/**
 * Navigation utilities for query parameter-based routing
 * This approach keeps the URL path intact while using query parameters for navigation state
 */

export interface NavigationParams {
  view?: 'search' | 'details';
  query?: string;
  konttorinro?: string;
  konttorioyno?: string;
  [key: string]: string | undefined;
}

/**
 * Navigates to a view using query parameters
 * @param setSearchParams - React Router's setSearchParams function
 * @param params - Navigation parameters
 */
export const navigateToView = (
  setSearchParams: (params: Record<string, string>) => void,
  params: NavigationParams
) => {
  const searchParams: Record<string, string> = {};

  // Add all defined parameters to search params
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams[key] = value;
    }
  });

  setSearchParams(searchParams);
};

/**
 * Navigates to the search view
 * @param setSearchParams - React Router's setSearchParams function
 * @param query - Optional search query to maintain
 */
export const navigateToSearch = (
  setSearchParams: (params: Record<string, string>) => void,
  query?: string
) => {
  const params: Record<string, string> = {};
  if (query) {
    params.query = query;
  }
  setSearchParams(params);
};

/**
 * Gets the current view from search parameters
 * @param searchParams - URLSearchParams object
 * @returns Current view ('search' or 'details')
 */
export const getCurrentView = (
  searchParams: URLSearchParams
): 'search' | 'details' => {
  // Determine view based on presence of office details parameters
  const konttorinro = searchParams.get('konttorinro');
  const konttorioyno = searchParams.get('konttorioyno');

  // If we have both office parameters, show details view
  return konttorinro && konttorioyno ? 'details' : 'search';
};

/**
 * Gets office details parameters from search params
 * @param searchParams - URLSearchParams object
 * @returns Object with branchId and companyId if available
 */
export const getOfficeDetailsParams = (searchParams: URLSearchParams) => {
  return {
    konttorinro: searchParams.get('konttorinro'),
    konttorioyno: searchParams.get('konttorioyno')
  };
};

/**
 * Gets search query from search params
 * @param searchParams - URLSearchParams object
 * @returns Search query string if available
 */
export const getSearchQuery = (
  searchParams: URLSearchParams
): string | null => {
  return searchParams.get('query');
};
